/**
 * `orgFinances` collection — studio-level overhead (rent, salaries,
 * utilities, marketing, etc.) and incidental income. Lives at the
 * top level of Firestore discriminated by `orgId`.
 *
 * Web port of mobile `src/features/finances/finances.ts` +
 * `src/features/finances/types.ts`. Same Firestore docs; the two
 * clients agree on shape.
 *
 * Permission: gated by Firestore rule `canFinance(orgId)` which is
 * Super Admin / Admin / Manager / Accountant. Manager-write was added
 * with the role expansion (see `docs/roles-and-permissions.md`).
 */
import {
  Timestamp,
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
  type DocumentReference,
} from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';

import { auth, db } from '@/lib/firebase';

// ───── Types ─────────────────────────────────────────────────

export type OrgFinanceKind = 'expense' | 'income';

/** Built-in category keys. Stored exactly as the enum string on
 *  the doc. CUSTOM categories (added via the picker's "+ Add new"
 *  affordance) store their LITERAL LABEL on the doc instead — so
 *  `OrgFinance.category` is broadened to `string` everywhere it
 *  appears. Display layer falls back to showing the raw string when
 *  it doesn't match a built-in key. */
export type OrgFinanceBuiltInCategory =
  | 'salary'
  | 'rent'
  | 'utilities'
  | 'internet'
  | 'office_supplies'
  | 'software'
  | 'travel'
  | 'marketing'
  | 'professional_fees'
  | 'other';

/** Effective on-doc category — built-in enum OR custom-category
 *  label string. */
export type OrgFinanceCategory = OrgFinanceBuiltInCategory | string;

export type OrgFinancePaymentMethod = 'cash' | 'bank' | 'upi' | 'card';

export const ORG_FINANCE_CATEGORIES: { key: OrgFinanceBuiltInCategory; label: string }[] = [
  { key: 'salary', label: 'Salary' },
  { key: 'rent', label: 'Rent' },
  { key: 'utilities', label: 'Utilities' },
  { key: 'internet', label: 'Internet' },
  { key: 'office_supplies', label: 'Office supplies' },
  { key: 'software', label: 'Software' },
  { key: 'travel', label: 'Travel' },
  { key: 'marketing', label: 'Marketing' },
  { key: 'professional_fees', label: 'Professional fees' },
  { key: 'other', label: 'Other' },
];

export const ORG_FINANCE_PAYMENT_METHODS: { key: OrgFinancePaymentMethod; label: string }[] = [
  { key: 'cash', label: 'Cash' },
  { key: 'bank', label: 'Bank transfer' },
  { key: 'upi', label: 'UPI' },
  { key: 'card', label: 'Card' },
];

export type OrgFinance = {
  id: string;
  orgId: string;
  kind: OrgFinanceKind;
  category: OrgFinanceCategory;
  amount: number;
  paidAt: { toMillis(): number } | null;
  payee?: string;
  payeeUid?: string | null;
  paymentMethod?: OrgFinancePaymentMethod;
  note?: string;
  createdBy: string;
  createdAt: { toMillis(): number } | null;
  updatedAt: { toMillis(): number } | null;
};

export type CreateOrgFinanceInput = {
  orgId: string;
  kind: OrgFinanceKind;
  category: OrgFinanceCategory;
  amount: number;
  paidAt: Date;
  payee?: string;
  payeeUid?: string | null;
  paymentMethod?: OrgFinancePaymentMethod;
  note?: string;
  createdBy: string;
};

// ───── CRUD ─────────────────────────────────────────────────

function requireUser(): string {
  const u = auth.currentUser;
  if (!u) throw new Error('You must be signed in.');
  return u.uid;
}

export async function createOrgFinance(input: CreateOrgFinanceInput): Promise<string> {
  requireUser();
  const ref = await addDoc(collection(db, 'orgFinances'), {
    orgId: input.orgId,
    kind: input.kind,
    category: input.category,
    amount: input.amount,
    paidAt: Timestamp.fromDate(input.paidAt),
    payee: input.payee ?? '',
    payeeUid: input.payeeUid ?? null,
    paymentMethod: input.paymentMethod ?? 'bank',
    note: input.note ?? '',
    createdBy: input.createdBy,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateOrgFinance(
  id: string,
  patch: Partial<
    Pick<
      OrgFinance,
      'kind' | 'category' | 'amount' | 'payee' | 'payeeUid' | 'paymentMethod' | 'note'
    >
  > & { paidAt?: Date },
): Promise<void> {
  requireUser();
  const ref: DocumentReference = doc(db, 'orgFinances', id);
  const data: Record<string, unknown> = { updatedAt: serverTimestamp() };
  if (patch.kind !== undefined) data.kind = patch.kind;
  if (patch.category !== undefined) data.category = patch.category;
  if (patch.amount !== undefined) data.amount = patch.amount;
  if (patch.payee !== undefined) data.payee = patch.payee;
  if (patch.payeeUid !== undefined) data.payeeUid = patch.payeeUid;
  if (patch.paymentMethod !== undefined) data.paymentMethod = patch.paymentMethod;
  if (patch.note !== undefined) data.note = patch.note;
  if (patch.paidAt !== undefined) data.paidAt = Timestamp.fromDate(patch.paidAt);
  await updateDoc(ref, data);
}

export async function deleteOrgFinance(id: string): Promise<void> {
  requireUser();
  await deleteDoc(doc(db, 'orgFinances', id));
}

// ───── Live read hook ───────────────────────────────────────

export type UseOrgFinancesResult = {
  data: OrgFinance[];
  loading: boolean;
};

export function useOrgFinances(
  orgId: string | null | undefined,
  opts?: { limit?: number },
): UseOrgFinancesResult {
  const [data, setData] = useState<OrgFinance[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orgId) {
      setData([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    return onSnapshot(
      query(collection(db, 'orgFinances'), where('orgId', '==', orgId)),
      (snap) => {
        const rows: OrgFinance[] = snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<OrgFinance, 'id'>),
        }));
        // Sort newest-first by paidAt client-side (matches mobile).
        rows.sort((a, b) => {
          const am = a.paidAt?.toMillis?.() ?? 0;
          const bm = b.paidAt?.toMillis?.() ?? 0;
          return bm - am;
        });
        const limited = opts?.limit ? rows.slice(0, opts.limit) : rows;
        setData(limited);
        setLoading(false);
      },
      (err) => {
        console.warn('[useOrgFinances] snapshot error:', err);
        setData([]);
        setLoading(false);
      },
    );
  }, [orgId, opts?.limit]);

  return { data, loading };
}

// ───── Month-to-date totals ────────────────────────────────

export type OrgFinanceMonthTotals = {
  income: number;
  expense: number;
  net: number;
  salaries: number;
};

export type UseOrgFinancesTotalsResult = {
  mtd: OrgFinanceMonthTotals;
  loading: boolean;
};

export function useOrgFinancesTotals(
  orgId: string | null | undefined,
): UseOrgFinancesTotalsResult {
  const { data, loading } = useOrgFinances(orgId);

  const mtd = useMemo<OrgFinanceMonthTotals>(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
    let income = 0;
    let expense = 0;
    let salaries = 0;
    for (const row of data) {
      const paid = row.paidAt?.toMillis?.() ?? 0;
      if (paid < start) continue;
      if (row.kind === 'income') income += row.amount;
      else {
        expense += row.amount;
        if (row.category === 'salary') salaries += row.amount;
      }
    }
    return { income, expense, net: income - expense, salaries };
  }, [data]);

  return { mtd, loading };
}
